import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Check, Landmark, Globe, Signature } from 'lucide-react';
import SeoMeta from '../components/SeoMeta';
import { useLocalizedUrl } from '../utils/urlHelper';
import { PageHero, SectionIntro, AnnotatedDocument, FaqList, faqSchema, ClosingCta, usePrimaryCta } from '../components/common/B2B';

// eCMR (nov-2026), contenido diseñado desde cero (Jesús: «antes o después tenemos
// que hacerlo»). Era la 3.ª de Google para «eCMR» con los textos antiguos
// (namespace i18n `ecmr`), así que se conservan las señales que posicionaban:
// la URL, «eCMR» al principio del título y del H1, «qué es», «validez legal» y
// «obligatorio en España» en el título, y la pregunta «¿Es obligatorio el eCMR
// en España?». Sin secciones que se repitan: el recorrido incluye lo que cambia
// frente al papel. Sin énfasis en el 5 de octubre.
// Imágenes: renderizador real con datos ficticios
// (marketing/documentos-muestra/render-ecmr-clasico.mjs).

const PAGE_URL = { es: 'https://www.bluecmr.com/documentos/ecmr', en: 'https://www.bluecmr.com/en/documents/ecmr' };

// Marcas sobre ecmr-clasico-completo.webp (en % de la imagen; posiciones medidas
// en el HTML del renderizador): QR, casilla 1, 6, 3, 8, 10-15, 20, 22 y pie.
// En inglés, el mismo documento pintado en inglés (ecmr-clasico-completo-en.webp) y
// las marcas trasladadas caja a caja desde el render español (4-nov-2026).
const MARKS = {
  es: [
    { x: 87.7, y: 4.6 }, { x: 34.9, y: 11.8 }, { x: 91.9, y: 16.5 }, { x: 44.2, y: 31.0 }, { x: 73.3, y: 34.6 },
    { x: 62.8, y: 55.1 }, { x: 95.0, y: 77.6 }, { x: 27.9, y: 91.3 }, { x: 81.4, y: 97.2 },
  ],
  en: [
    { x: 87.7, y: 4.6 }, { x: 34.9, y: 11.8 }, { x: 91.9, y: 16.5 }, { x: 44.2, y: 31 }, { x: 73.3, y: 34.6 },
    { x: 62.8, y: 55 }, { x: 95, y: 76.4 }, { x: 27.9, y: 91.3 }, { x: 81.4, y: 97.2 },
  ],
};
const DOC_IMG = {
  es: { hero: '/images/documentos/ecmr-clasico.webp', full: '/images/documentos/ecmr-clasico-completo.webp', h: 1624 },
  en: { hero: '/images/documentos/ecmr-clasico-en.webp', full: '/images/documentos/ecmr-clasico-completo-en.webp', h: 1626 },
};

const COPY = {
  es: {
    seoTitle: 'eCMR: qué es, validez legal y si es obligatorio en España | BlueCMR',
    seoDescription: 'El eCMR es el CMR en formato electrónico, con la misma validez que el papel según el Protocolo eCMR: cómo funciona, dónde vale y si es obligatorio en España.',
    seoKeywords: 'ecmr, qué es el ecmr, ecmr validez legal, ecmr obligatorio españa, cmr electrónico, carta de porte internacional electrónica, protocolo ecmr',
    heroEyebrow: 'eCMR · Transporte internacional',
    heroTitle: 'eCMR: la carta de porte internacional, en formato electrónico',
    heroText: 'Emita, firme y conserve el CMR en digital conforme al Protocolo eCMR, con la misma validez que el papel en los países que lo han ratificado.',
    heroChecks: ['Convenio CMR y Protocolo eCMR', '42 Partes', 'Firma electrónica avanzada'],
    heroAlt: 'eCMR de BlueCMR en diseño clásico, con las casillas de la carta de porte CMR',
    platform: 'Conozca la plataforma',
    whatEyebrow: 'Qué es',
    whatTitle: '¿Qué es el eCMR?',
    whatText: 'El eCMR es la carta de porte del Convenio CMR en formato electrónico. Formaliza el contrato de transporte internacional de mercancías por carretera y acompaña a la mercancía, igual que el CMR en papel, pero sin ejemplares impresos.',
    facts: [
      { icon: Landmark, title: 'Misma validez que el papel', text: 'El Protocolo adicional al Convenio CMR (Ginebra, 2008) equipara el soporte electrónico al papel. España se adhirió en 2011.' },
      { icon: Globe, title: '42 Partes', text: 'Vale en los países que han ratificado el Protocolo: 42 Partes a noviembre de 2026 según la lista oficial de la ONU, la mayoría de Europa. Las últimas, Albania, Macedonia del Norte, Montenegro y Serbia.' },
      { icon: Signature, title: 'Quién firma', text: 'El expedidor y el transportista (art. 5 del Convenio); el destinatario firma la recepción (art. 13).' },
    ],
    howEyebrow: 'Cómo funciona',
    howTitle: 'Del muelle al destino, sin papel',
    steps: [
      { place: 'Oficina', title: 'Emisión', text: 'Desde su TMS o ERP por API o con plantillas: nadie rellena el impreso a mano.' },
      { place: 'Carga', title: 'Firma', text: 'Expedidor y transportista firman en el móvil con firma electrónica avanzada; no hay ejemplares que repartir.' },
      { place: 'En ruta', title: 'Seguimiento', text: 'Reservas e incidencias con hora y fotografía; en un control, la autoridad abre el documento con el QR.' },
      { place: 'Destino', title: 'Entrega', text: 'El destinatario firma la recepción y todas las partes lo ven al momento, no cuando vuelve el papel.' },
      { place: 'Después', title: 'Archivo', text: 'Copia firmada para todas las partes y conservación con sus evidencias, con los datos listos para eFTI.' },
    ],
    docEyebrow: 'El documento',
    docTitle: 'El CMR de siempre, casilla a casilla',
    docText: 'Así es un eCMR emitido con BlueCMR (datos de ejemplo): la misma estructura que el impreso CMR, con los rótulos en los idiomas del transporte.',
    docAlt: 'eCMR completo emitido con BlueCMR, con datos de ejemplo',
    notes: [
      { title: 'Código QR', text: 'Cualquier autoridad abre el documento y comprueba sus firmas.' },
      { title: 'Remitente y destinatario', text: 'Nombre, dirección y país (casillas 1 y 2).' },
      { title: 'Transportista y vehículo', text: 'Con matrículas y conductor; si cambian en ruta, el documento se actualiza.' },
      { title: 'Toma de carga y entrega', text: 'Lugares, países y fechas (casillas 3 y 4).' },
      { title: 'Reservas del transportista', text: 'Las que haga al hacerse cargo de la mercancía, con hora y firma (casilla 8).' },
      { title: 'Mercancía', text: 'Marcas, bultos, embalaje, naturaleza, peso y volumen (casillas 10 a 15).' },
      { title: 'Sometimiento al Convenio CMR', text: 'La cláusula que exige el artículo 6, en los idiomas del documento (casilla 20).' },
      { title: 'Firmas', text: 'Remitente y transportista firman; el destinatario, la recepción (casillas 22 a 24).' },
      { title: 'Carta de porte electrónica', text: 'Identificada conforme al Protocolo adicional al Convenio CMR, con su dirección de verificación.' },
    ],
    faqEyebrow: 'Preguntas frecuentes',
    faqTitle: 'Dudas habituales sobre el eCMR',
    faq: [
      { q: '¿Es obligatorio el eCMR en España?', a: 'No. El eCMR es la alternativa electrónica al CMR en papel y su uso es voluntario. Lo obligatorio en formato electrónico en España es el documento de control (DeCA) del transporte nacional. En el internacional basta con el CMR o el eCMR; en un cabotaje, un eCMR que incluya los datos del documento de control puede sustituirlo.' },
      { q: '¿Qué requisitos técnicos debe cumplir?', a: 'Garantizar la autenticidad, la integridad y la accesibilidad del documento, según el Protocolo adicional de 2008. BlueCMR lo hace con firma electrónica avanzada eIDAS, sellado de tiempo y acceso por código QR.' },
      { q: '¿Puede incluir mercancías peligrosas?', a: 'Sí. En los transportes internacionales de mercancías peligrosas, la información del documento de transporte ADR puede ir en el propio eCMR.' },
      { q: '¿Y si el transporte es nacional?', a: 'Entonces el documento es la carta de porte de la Ley 15/2009 y, en España, el DeCA. BlueCMR emite todos con la misma operativa.' },
    ],
    relatedTitle: 'Otros documentos',
    related: [
      { to: '/documentos/carta-porte', title: 'Carta de porte', text: 'Para el transporte nacional: contrato, DeCA y prueba de entrega.' },
      { to: '/documentos/documento-control', title: 'DeCA', text: 'El documento de control digital del transporte nacional.' },
      { to: '/documentos/transporte-adr', title: 'Documento de transporte ADR', text: 'Para mercancías peligrosas.' },
    ],
    closing: 'Transporte internacional sin papel y con plena validez legal',
    closingText: 'Le enseñamos el eCMR con sus rutas y sus clientes, sin compromiso.',
    breadcrumb: ['Inicio', 'Documentos'],
  },
  en: {
    seoTitle: 'eCMR: what it is, legal validity and how it works | BlueCMR',
    seoDescription: 'The eCMR is the electronic CMR consignment note, legally equivalent to paper under the eCMR Protocol: how it works, where it is valid and how BlueCMR issues it.',
    seoKeywords: 'ecmr, what is ecmr, ecmr legal validity, electronic cmr, electronic consignment note, ecmr protocol',
    heroEyebrow: 'eCMR · International transport',
    heroTitle: 'eCMR: the international consignment note, in electronic form',
    heroText: 'Issue, sign and preserve the CMR digitally under the eCMR Protocol, with the same validity as paper in the countries that have ratified it.',
    heroChecks: ['CMR Convention and eCMR Protocol', '42 Parties', 'Advanced electronic signature'],
    heroAlt: 'BlueCMR eCMR in classic layout, with the CMR consignment note boxes',
    platform: 'Explore the platform',
    whatEyebrow: 'What it is',
    whatTitle: 'What is the eCMR?',
    whatText: 'The eCMR is the CMR Convention consignment note in electronic form. It formalises the contract for the international carriage of goods by road and travels with the goods, just like the paper CMR, but without printed copies.',
    facts: [
      { icon: Landmark, title: 'Same validity as paper', text: 'The Additional Protocol to the CMR Convention (Geneva, 2008) puts electronic and paper format on an equal footing. Spain acceded to it in 2011.' },
      { icon: Globe, title: '42 Parties', text: 'Valid in the countries that have ratified the Protocol: 42 Parties as of November 2026 according to the official UN list, most of Europe. The latest are Albania, North Macedonia, Montenegro and Serbia.' },
      { icon: Signature, title: 'Who signs', text: 'The sender and the carrier (art. 5 of the Convention); the consignee signs the receipt (art. 13).' },
    ],
    howEyebrow: 'How it works',
    howTitle: 'From the dock to the destination, without paper',
    steps: [
      { place: 'Office', title: 'Issue', text: 'From your TMS or ERP through the API or with templates: nobody fills in the form by hand.' },
      { place: 'Loading', title: 'Signature', text: 'Sender and carrier sign on the phone with an advanced electronic signature; no copies to hand out.' },
      { place: 'On the road', title: 'Tracking', text: 'Reservations and incidents with time and photograph; at a check, the authority opens the document with the QR code.' },
      { place: 'Destination', title: 'Delivery', text: 'The consignee signs the receipt and every party sees it straight away, not when the paper comes back.' },
      { place: 'Afterwards', title: 'Archive', text: 'A signed copy for every party and preservation with its evidence, with the data ready for eFTI.' },
    ],
    docEyebrow: 'The document',
    docTitle: 'The CMR you know, box by box',
    docText: 'This is an eCMR issued with BlueCMR (sample data): the same structure as the CMR form, with labels in the languages of the transport.',
    docAlt: 'Full eCMR issued with BlueCMR, with sample data',
    notes: [
      { title: 'QR code', text: 'Any authority opens the document and checks its signatures.' },
      { title: 'Sender and consignee', text: 'Name, address and country (boxes 1 and 2).' },
      { title: 'Carrier and vehicle', text: 'With plates and driver; if they change on the road, the document is updated.' },
      { title: 'Taking over and delivery', text: 'Places, countries and dates (boxes 3 and 4).' },
      { title: 'Carrier’s reservations', text: 'Those made on taking over the goods, with time and signature (box 8).' },
      { title: 'Goods', text: 'Marks, packages, packaging, nature, weight and volume (boxes 10 to 15).' },
      { title: 'Subject to the CMR Convention', text: 'The clause required by article 6, in the languages of the document (box 20).' },
      { title: 'Signatures', text: 'Sender and carrier sign; the consignee signs the receipt (boxes 22 to 24).' },
      { title: 'Electronic consignment note', text: 'Identified under the Additional Protocol to the CMR Convention, with its verification address.' },
    ],
    faqEyebrow: 'FAQ',
    faqTitle: 'Common questions about the eCMR',
    faq: [
      { q: 'Is the eCMR mandatory in Spain?', a: 'No. The eCMR is the electronic alternative to the paper CMR and its use is voluntary. What is mandatory in electronic form in Spain is the control document (DeCA) for domestic transport. For international transport the CMR or eCMR is enough; in cabotage, an eCMR that includes the control document data can replace it.' },
      { q: 'What technical requirements must it meet?', a: 'Guaranteeing the authenticity, integrity and accessibility of the document, under the 2008 Additional Protocol. BlueCMR does so with an eIDAS advanced electronic signature, timestamping and access by QR code.' },
      { q: 'Can it include dangerous goods?', a: 'Yes. In international transport of dangerous goods, the ADR transport document information can go in the eCMR itself.' },
      { q: 'What if the transport is domestic?', a: 'Then the document is the national consignment note and, in Spain, the control document (DeCA). BlueCMR issues them all with the same workflow.' },
    ],
    relatedTitle: 'Other documents',
    related: [
      { to: '/en/documents/road-waybill', title: 'Consignment note (Spain)', text: 'For domestic transport: contract, control document and proof of delivery.' },
    ],
    closing: 'International transport without paper and with full legal validity',
    closingText: 'We will show you the eCMR with your routes and customers, with no commitment.',
    breadcrumb: ['Home', 'Documents'],
  },
};

const ECMRDocumentoPage = () => {
  const { i18n } = useTranslation();
  const lang = i18n.language === 'es' ? 'es' : 'en';
  const c = COPY[lang];
  const { getLocalizedUrl } = useLocalizedUrl();
  const primaryCta = usePrimaryCta(lang);
  const notes = c.notes.map((n, i) => ({ ...n, ...MARKS[lang][i] }));

  return (
    <>
      <SeoMeta
        title={c.seoTitle}
        description={c.seoDescription}
        canonical={PAGE_URL[lang]}
        type="website"
        alternates={{ es: PAGE_URL.es, en: PAGE_URL.en, 'x-default': PAGE_URL.es }}
        keywords={c.seoKeywords}
        breadcrumbs={[
          { name: c.breadcrumb[0], url: lang === 'es' ? 'https://www.bluecmr.com/' : 'https://www.bluecmr.com/en' },
          { name: c.breadcrumb[1], url: 'https://www.bluecmr.com/documentos' },
          { name: 'eCMR', url: PAGE_URL[lang] }
        ]}
        structuredData={[
          { '@context': 'https://schema.org', '@type': 'WebPage', name: c.heroTitle, description: c.seoDescription, url: PAGE_URL[lang] },
          faqSchema(c.faq)
        ]}
      />

      <div className="bg-white">
        <PageHero
          eyebrow={c.heroEyebrow}
          title={c.heroTitle}
          text={c.heroText}
          documentImage={{ src: DOC_IMG[lang].hero, alt: c.heroAlt }}
          primary={primaryCta}
          secondary={{ to: getLocalizedUrl('/plataforma'), label: c.platform }}
          compact
        >
          <ul className="mt-8 flex flex-wrap gap-x-6 gap-y-2">
            {c.heroChecks.map((item) => (
              <li key={item} className="flex items-center gap-2 text-slate-200">
                <Check className="w-4 h-4 text-blue-300" strokeWidth={2.5} />
                {item}
              </li>
            ))}
          </ul>
        </PageHero>

        {/* ¿Qué es el eCMR? */}
        <section className="py-20 md:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 mb-14">
              <div className="lg:col-span-5">
                <SectionIntro eyebrow={c.whatEyebrow} title={c.whatTitle} className="" />
              </div>
              <p className="lg:col-span-7 text-lg text-slate-600 leading-relaxed">{c.whatText}</p>
            </div>
            <div className="grid md:grid-cols-3 gap-x-10 gap-y-10">
              {c.facts.map((item) => {
                const Icon = item.icon;
                return (
                  <div key={item.title} className="border-t-2 border-slate-900 pt-6">
                    <Icon className="w-6 h-6 text-primary-700 mb-4" strokeWidth={1.5} />
                    <h3 className="font-semibold text-slate-900">{item.title}</h3>
                    <p className="mt-2 text-[15px] text-slate-600 leading-relaxed">{item.text}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Cómo funciona (con lo que cambia frente al papel) */}
        <section className="py-20 md:py-24 bg-slate-50 border-y border-slate-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionIntro eyebrow={c.howEyebrow} title={c.howTitle} />
            <ol className="grid sm:grid-cols-2 lg:grid-cols-5 gap-x-8 gap-y-10">
              {c.steps.map((step, i) => (
                <li key={step.title} className="border-t-2 border-slate-900 pt-5">
                  <p className="flex items-baseline gap-2 text-xs font-semibold uppercase tracking-[0.14em] text-primary-700">
                    <span className="tabular-nums">{String(i + 1).padStart(2, '0')}</span>
                    <span>{step.place}</span>
                  </p>
                  <h3 className="mt-3 font-semibold text-slate-900">{step.title}</h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">{step.text}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* El eCMR real, anotado */}
        <section className="py-20 md:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionIntro eyebrow={c.docEyebrow} title={c.docTitle} text={c.docText} />
            <AnnotatedDocument
              src={DOC_IMG[lang].full}
              alt={c.docAlt}
              width="1100"
              height={DOC_IMG[lang].h}
              notes={notes}
            />
          </div>
        </section>

        {/* Preguntas frecuentes */}
        <section className="py-20 md:py-24 bg-slate-50 border-t border-slate-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10 lg:gap-16">
            <div className="lg:col-span-4">
              <SectionIntro eyebrow={c.faqEyebrow} title={c.faqTitle} className="" />
            </div>
            <div className="lg:col-span-8">
              <FaqList items={c.faq} />
            </div>
          </div>
        </section>

        {/* Otros documentos */}
        <section className="py-14 border-t border-slate-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 mb-6">{c.relatedTitle}</h2>
            <ul className="grid md:grid-cols-3 gap-x-8">
              {c.related.map((r) => (
                <li key={r.to} className="border-t border-slate-200">
                  <Link to={r.to} className="group flex items-start justify-between gap-4 py-5">
                    <span>
                      <span className="block font-semibold text-slate-900 group-hover:text-primary-700 transition-colors">{r.title}</span>
                      <span className="mt-1 block text-sm text-slate-600">{r.text}</span>
                    </span>
                    <ArrowRight className="mt-1 w-5 h-5 flex-shrink-0 text-slate-400 group-hover:text-primary-700 group-hover:translate-x-1 transition" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <ClosingCta title={c.closing} text={c.closingText} lang={lang} />
      </div>
    </>
  );
};

export default ECMRDocumentoPage;
